import { httpClient } from '@/utils/httpClient';
import { entityService } from '@/services/EntityService';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { ASSET_STATUS_KEYS } from '@equip/utils';
import {
  buildPropertyBookListingReportHtml,
  type PropertyBookListingNsnGroup,
  type PropertyBookListingRow,
} from '@/reports/templates/propertyBookListingTemplate';

interface AssetInventorySummaryRow {
  id: string;
  location_id: string | null;
  location_name: string | null;
  location_path: string | null;
  lin: string | null;
  nsn: string | null;
  status_key: string | null;
  catalog_id: string | null;
  stock_quantity: number;
}

interface CatalogEntityData {
  size?: string;
  unit_price?: number | string;
  unit_of_issue?: string;
  arc?: string;
}

interface CatalogRow {
  id: string;
  display_name: string;
  entity_data?: CatalogEntityData;
}

const PAGE_SIZE = 1000;

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

async function fetchInventoryRows(
  locationId?: string,
  lin?: string,
): Promise<AssetInventorySummaryRow[]> {
  const allRows: AssetInventorySummaryRow[] = [];
  let lastId: string | undefined;

  while (true) {
    const searchParams = new URLSearchParams({
      status_key: `eq.${ASSET_STATUS_KEYS.AVAILABLE}`,
      order: 'id.asc',
      limit: String(PAGE_SIZE),
      select:
        'id,location_id,location_name,location_path,lin,nsn,status_key,catalog_id,stock_quantity',
    });
    if (locationId) {
      searchParams.set('location_id', `eq.${locationId}`);
    }
    if (lin) {
      searchParams.set('lin', `ilike.*${lin}*`);
    }
    if (lastId) {
      searchParams.set('id', `gt.${lastId}`);
    }

    const endpoint = `/api/rest/data/v1/asset_inventory_summary?${searchParams.toString()}`;
    const page = await httpClient.get<AssetInventorySummaryRow[]>(endpoint);
    allRows.push(...page);

    if (page.length < PAGE_SIZE) {
      break;
    }
    lastId = page[page.length - 1]!.id;
  }

  return allRows;
}

export const runPropertyBookListingReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const locationId = (params.locationId as string | undefined)?.trim() || undefined;
  const lin = (params.lin as string | undefined)?.trim() || undefined;

  const rows = await fetchInventoryRows(locationId, lin);

  const catalogIds = Array.from(
    new Set(rows.map((row) => row.catalog_id).filter((id): id is string => Boolean(id))),
  );

  const catalogById = new Map<string, CatalogRow>();
  for (const batch of chunk(catalogIds, 75)) {
    if (batch.length === 0) continue;
    const idFilter = batch.length === 1 ? `eq.${batch[0]}` : `in.(${batch.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batch.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,display_name,entity_data',
      },
    });
    catalogs.forEach((catalog) => catalogById.set(catalog.id, catalog as CatalogRow));
  }

  const rowsByNsn = new Map<string, PropertyBookListingRow[]>();

  rows.forEach((row) => {
    const catalog = row.catalog_id ? catalogById.get(row.catalog_id) : undefined;
    const unitPrice = parseNumber(catalog?.entity_data?.unit_price);
    const onHand = row.stock_quantity ?? 0;
    const totalPrice = unitPrice !== null ? unitPrice * onHand : null;
    const nsnKey = row.nsn?.trim() || '—';

    const entry: PropertyBookListingRow = {
      location: row.location_path || row.location_name || 'Unassigned',
      lin: row.lin,
      nsn: row.nsn,
      size: catalog?.entity_data?.size ?? null,
      nomenclature: catalog?.display_name ?? '—',
      unitOfIssue: catalog?.entity_data?.unit_of_issue ?? null,
      arc: catalog?.entity_data?.arc ?? null,
      onHand,
      unitPrice,
      totalPrice,
    };

    const list = rowsByNsn.get(nsnKey) ?? [];
    list.push(entry);
    rowsByNsn.set(nsnKey, list);
  });

  const groups: PropertyBookListingNsnGroup[] = Array.from(rowsByNsn.entries())
    .map(([nsn, groupRows]) => {
      groupRows.sort(
        (a, b) => a.location.localeCompare(b.location) || (a.lin ?? '').localeCompare(b.lin ?? ''),
      );

      let aslValue: number | null = null;
      groupRows.forEach((row) => {
        if (typeof row.totalPrice === 'number') {
          aslValue = (aslValue ?? 0) + row.totalPrice;
        }
      });

      return { nsn, rows: groupRows, aslValue };
    })
    .sort((a, b) => a.nsn.localeCompare(b.nsn));

  const html = buildPropertyBookListingReportHtml({
    generatedAt: new Date(),
    linFilter: lin ?? null,
    groups,
  });

  outputReport(html, 'property-book-listing.html', getOutputFormat(params));
};
